'use client';

/**
 * WEB-600 — `useShare`: native Web Share with copy-link fallback.
 *
 * Every share trigger calls `share()` from a click handler. The payload comes
 * only from `buildSharePayload` (canonical city-host URL, never fabricated
 * text). `navigator.share` is invoked synchronously inside the gesture — no
 * awaits before it — and the fallback is `copyLink`, which never throws.
 */

import { useNotification } from '@loci/store';
import { useCallback } from 'react';

import { track } from '@/lib/analytics';
import { copyLink } from '@/lib/share/clipboard';
import { buildSharePayload } from '@/lib/share/buildSharePayload';
import type { ShareEntityDescriptor } from '@/lib/share/types';

/** How a `share()` call ended — the only thing the telemetry event reports. */
type ShareOutcome = 'native' | 'dismissed' | 'copied' | 'failed';

/**
 * WEB-603 — one consent-gated `share_tapped` event per `share()` call, emitted
 * after the outcome is known. Carries only entity kind + outcome: never the
 * URL, slug, title or text.
 */
function trackShare(entityType: ShareEntityDescriptor['type'], outcome: ShareOutcome): void {
  track('share_tapped', { entityType, outcome });
}

export interface UseShareResult {
  /** Open the native share sheet, falling back to copying the canonical link. */
  share: () => Promise<void>;
}

/** The user dismissed the native sheet — a deliberate cancel, not a failure. */
function isAbortError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { name?: unknown }).name === 'AbortError'
  );
}

export function useShare(descriptor: ShareEntityDescriptor): UseShareResult {
  const { showNotification } = useNotification();

  const share = useCallback(async (): Promise<void> => {
    const { title, text, url } = buildSharePayload(descriptor);

    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      let nativeOutcome: 'native' | 'dismissed' | null = null;
      try {
        // First await in the function: `navigator.share` starts inside the gesture.
        await navigator.share({ title, url, ...(text ? { text } : {}) });
        nativeOutcome = 'native';
      } catch (error: unknown) {
        // AbortError = deliberate dismiss; anything else (NotAllowedError /
        // TypeError / DataError / …) falls through to the copy-link fallback.
        if (isAbortError(error)) nativeOutcome = 'dismissed';
      }
      // Emitted outside the try so a telemetry failure can never trigger the fallback.
      if (nativeOutcome) {
        trackShare(descriptor.type, nativeOutcome);
        return;
      }
    }

    const copied = await copyLink(url, showNotification);
    trackShare(descriptor.type, copied ? 'copied' : 'failed');
  }, [descriptor, showNotification]);

  return { share };
}
